import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, KeyRound, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import { API } from "@/lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async () => {
    if (!newPassword) { toast.error("Please enter a new password"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword }, { withCredentials: true });
      if (res.data.token) {
        localStorage.setItem("admin_token", res.data.token);
        toast.success("Password reset! Taking you to the dashboard...");
        navigate("/admin/dashboard");
      } else {
        setSuccess(true);
        toast.success("Password reset successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password. Token may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="bg-[#050505] min-h-screen flex items-center justify-center px-6">
        <div className="text-center space-y-4">
          <h1 className="font-heading text-3xl text-white">Invalid Reset Link</h1>
          <p className="text-zinc-400 text-sm">This link is missing a reset token.</p>
          <Link to="/admin" className="text-[#D4AF37] text-sm">Back to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <KeyRound className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-3xl tracking-tighter uppercase text-white">Reset Password</h1>
          <p className="text-zinc-500 text-sm mt-2">Enter your new password below</p>
        </div>

        {!success ? (
          <div className="space-y-4" data-testid="reset-form">
            <Input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="new-password-input"
            />
            <Input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="confirm-password-input"
            />
            <Button
              onClick={handleReset}
              disabled={loading}
              className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
              data-testid="reset-submit-button"
            >
              {loading ? "Resetting..." : "Set New Password"}
            </Button>
          </div>
        ) : (
          <div className="text-center space-y-6" data-testid="reset-success">
            <div className="w-16 h-16 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-white text-sm font-medium mb-2">Password Reset Complete</p>
              <p className="text-zinc-500 text-xs">You can now log in with your new password.</p>
            </div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm"
              data-testid="go-to-login-button"
            >
              Go to Login <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        <Link to="/admin" className="block text-center text-zinc-600 text-xs hover:text-zinc-400 transition-colors">
          <ArrowRight className="w-3 h-3 inline rotate-180 mr-1" /> Back to login
        </Link>
      </div>
    </div>
  );
}
